import { z } from 'zod';

import { travelReturnModeList } from '@/module/Travel/Settings/model/TravelSettingsTypes';

import { TravelReturnRequest, travelBoardOutcomeList } from './TravelApprovalTypes';

export const travelReturnSchema = z
  .object({
    reason: z.string().trim().min(1),
    returnMode: z.enum(travelReturnModeList).nullable(),
    notifyOtherApprovers: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.returnMode == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['returnMode'], params: { i18n: 'custom.required' } });
    }
  });

export type TravelReturnSchema = z.infer<typeof travelReturnSchema>;

// A decision file already attached in the current round stands in for a new one.
export function travelBoardDecisionSchema(fileRequired: boolean) {
  return z
    .object({
      outcome: z.enum(travelBoardOutcomeList).nullable(),
      file: z.custom<File | null>(value => value == null || value instanceof File),
      reason: z.string().trim(),
      note: z.string().trim(),
    })
    .superRefine((values, ctx) => {
      if (values.outcome == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['outcome'], params: { i18n: 'custom.required' } });
      }
      if (fileRequired && values.file == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['file'], params: { i18n: 'custom.fileRequired' } });
      }
      if (values.outcome === 'reject' && values.reason === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], params: { i18n: 'custom.required' } });
      }
    });
}

export type TravelBoardDecisionSchema = z.infer<ReturnType<typeof travelBoardDecisionSchema>>;

export function toTravelReturnRequest(values: TravelReturnSchema): TravelReturnRequest {
  return {
    reason: values.reason,
    returnMode: values.returnMode,
    // A full re-run makes every earlier approver decide again, so the server sends no mail and the flag never claims otherwise.
    notifyOtherApprovers: values.returnMode === 'STRAIGHT_BACK' && values.notifyOtherApprovers,
  };
}
